<script setup>
import { router } from '@inertiajs/vue3';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { onMounted, reactive, ref } from 'vue';
import { array, boolean, object, string } from 'yup';

const { tenant, externalService } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    externalService: {
        required: true,
        type: Object,
    },
});

const { route } = useRoute();

const cms = [
    { value: 'Magento', label: 'Magento' },
    { value: 'Prestashop', label: 'Prestashop' },
    { value: 'Shopify', label: 'Shopify' },
    { value: 'WooCommerce', label: 'WooCommerce' },
    { value: 'Hiboutik', label: 'Hiboutik' },
];

const dataTypes = [
    { value: 'all', label: 'Tout' },
    { value: 'only_store', label: 'Uniquement boutique' },
    { value: 'only_marketplace', label: 'Uniquement marketplace' },
    { value: 'only_point_of_sales', label: 'Uniquement point de vente' },
];

const currencyCodes = [
    { value: 'EUR', label: 'EUR' },
    { value: 'XPF', label: 'XPF' },
];

const stores = ref([]);

const languages = ref([]);

const productAttributes = ref([]);

const productFeatures = ref([]);

const loading = ref(true);

const isSubmitting = ref(false);

const hasBaseValues = ref(false);

const form = reactive({
    active: externalService.active,
    cms: externalService.data.cms
        ? cms.find((c) => c.value === externalService.data.cms) || cms[0]
        : cms[0],
    url: externalService.data.url || '',
    storeName: externalService.data.storeName || '',
    key: externalService.data.key || '',
    adminApiAccessToken: externalService.data.adminApiAccessToken || '',
    consumerKey: externalService.data.consumerKey || '',
    consumerSecret: externalService.data.consumerSecret || '',
    account: externalService.data.account || '',
    apiLogin: externalService.data.apiLogin || '',
    apiKey: externalService.data.apiKey || '',
    dataType: externalService.data.dataType
        ? dataTypes.find((d) => d.value === externalService.data.dataType) ||
          dataTypes[0]
        : dataTypes[0],
    currencyCode: externalService.data.currencyCode
        ? currencyCodes.find(
              (c) => c.value === externalService.data.currencyCode,
          ) || currencyCodes[0]
        : currencyCodes[0],
    stores: [],
    store: null,
    defaultLanguage: null,
    defaultStore: null,
    productAttributes: [],
    productFeatures: [],
    doNotUseInvoices: externalService.data.doNotUseInvoices,
});

const validationSchema = object({
    active: boolean().required('Ce champ est requis.'),
    cms: object().required('Ce champ est requis.'),
    url: string().when('cms', {
        is: (val) =>
            val &&
            (val.value === 'Magento' ||
                val.value === 'Prestashop' ||
                val.value === 'WooCommerce'),
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    storeName: string().when('cms', {
        is: (val) => val && val.value === 'Shopify',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    key: string().when('cms', {
        is: (val) =>
            val && (val.value === 'Magento' || val.value === 'Prestashop'),
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    adminApiAccessToken: string().when('cms', {
        is: (val) => val && val.value === 'Shopify',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    consumerKey: string().when('cms', {
        is: (val) => val && val.value === 'WooCommerce',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    consumerSecret: string().when('cms', {
        is: (val) => val && val.value === 'WooCommerce',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    account: string().when('cms', {
        is: (val) => val && val.value === 'Hiboutik',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    apiLogin: string().when('cms', {
        is: (val) => val && val.value === 'Hiboutik',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    apiKey: string().when('cms', {
        is: (val) => val && val.value === 'Hiboutik',
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    dataType: object().when('cms', {
        is: (val) =>
            val && (val.value === 'Magento' || val.value === 'Prestashop'),
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    currencyCode: object().required('Ce champ est requis.'),
    stores: array().when('cms', {
        is: (val) =>
            val && (val.value === 'Magento' || val.value === 'Prestashop'),
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    store: object().when('cms', {
        is: (val) =>
            val &&
            (val.value === 'Shopify' ||
                val.value === 'WooCommerce' ||
                val.value === 'Hiboutik'),
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    defaultLanguage: object().required('Ce champ est requis.'),
    defaultStore: object().when('cms', {
        is: (val) =>
            val && (val.value === 'Magento' || val.value === 'Prestashop'),
        then: (schema) => schema.required('Ce champ est requis.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    doNotUseInvoices: boolean().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.externalServices.postECommerce',
                {
                    tenant: tenant.id,
                },
            ),
            {
                active: form.active,
                cms: form.cms.value,
                url: form.url,
                storeName: form.storeName,
                key: form.key,
                adminApiAccessToken: form.adminApiAccessToken,
                consumerKey: form.consumerKey,
                consumerSecret: form.consumerSecret,
                account: form.account,
                apiLogin: form.apiLogin,
                apiKey: form.apiKey,
                storeIds: form.stores.map((store) => store.id),
                storeId: form.store ? form.store.id : null,
                defaultLanguageCode: form.defaultLanguage
                    ? form.defaultLanguage.code
                    : null,
                defaultStoreId: form.defaultStore ? form.defaultStore.id : null,
                dataType: form.dataType.value,
                currencyCode: form.currencyCode.value,
                productAttributeCodes: form.productAttributes.map(
                    (attribute) => attribute.code,
                ),
                productFeatureCodes: form.productFeatures.map(
                    (feature) => feature.code,
                ),
                doNotUseInvoices: form.doNotUseInvoices,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.customer.externalServices.eCommerce',
                    {
                        tenant: tenant.id,
                    },
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.customer.externalServices'),
            );
        });
}

function updateData(initialize = false) {
    loading.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.externalServices.postECommerceData',
                {
                    tenant: tenant.id,
                },
            ),
            {
                cms: form.cms.value,
                url: form.url,
                storeName: form.storeName,
                key: form.key,
                adminApiAccessToken: form.adminApiAccessToken,
                consumerKey: form.consumerKey,
                consumerSecret: form.consumerSecret,
                account: form.account,
                apiLogin: form.apiLogin,
                apiKey: form.apiKey,
            },
        )
        .then((response) => {
            const data = response.data;

            if ('stores' in data) {
                stores.value = data.stores || [];

                if (initialize) {
                    if (
                        form.cms.value === 'Magento' ||
                        form.cms.value === 'Prestashop'
                    ) {
                        form.stores = data.stores.filter((store) =>
                            externalService.data.storeIds.includes(store.id),
                        );
                    }

                    if (
                        form.cms.value === 'Shopify' ||
                        form.cms.value === 'WooCommerce' ||
                        form.cms.value === 'Hiboutik'
                    ) {
                        form.store = data.stores.find(
                            (store) =>
                                store.id === externalService.data.storeId,
                        );
                    }
                }

                if (
                    (form.cms.value === 'Magento' ||
                        form.cms.value === 'Prestashop') &&
                    form.stores.length === 0 &&
                    data.stores.length > 0
                ) {
                    form.stores.push(data.stores[0]);
                }

                if (
                    (form.cms.value === 'Shopify' ||
                        form.cms.value === 'WooCommerce' ||
                        form.cms.value === 'Hiboutik') &&
                    data.stores.length > 0
                ) {
                    form.store = data.stores[0];
                }

                if (initialize) {
                    form.defaultStore = data.stores.find(
                        (store) =>
                            store.id === externalService.data.defaultStoreId,
                    );
                }

                if (!form.defaultStore && data.stores.length > 0) {
                    form.defaultStore = data.stores[0];
                }
            }

            if ('languages' in data) {
                languages.value = data.languages || [];

                if (initialize) {
                    form.defaultLanguage = data.languages.find(
                        (language) =>
                            language.code ===
                            externalService.data.defaultLanguageCode,
                    );
                }

                if (!form.defaultLanguage && data.languages.length > 0) {
                    form.defaultLanguage = data.languages[0];
                }
            }

            if ('productAttributes' in data) {
                productAttributes.value = data.productAttributes || [];

                if (initialize) {
                    form.productAttributes = data.productAttributes.filter(
                        (attribute) =>
                            externalService.data.productAttributeCodes.includes(
                                attribute.code,
                            ),
                    );
                }
            }

            if ('productFeatures' in data) {
                productFeatures.value = data.productFeatures || [];

                if (initialize) {
                    form.productFeatures = data.productFeatures.filter(
                        (feature) =>
                            externalService.data.productFeatureCodes.includes(
                                feature.code,
                            ),
                    );
                }
            }

            hasBaseValues.value =
                stores.value.length > 0 && languages.value.length > 0;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    updateData(true);
});
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
        :loading="loading"
    >
        <FormColumn>
            <FormContent>
                <SwitchField
                    v-model="form.active"
                    label="Actif"
                    field-name="active"
                    field-id="active"
                />
            </FormContent>

            <FormContent>
                <FormGrid :columns="3">
                    <ListboxField
                        v-model="form.cms"
                        field-id="cms"
                        field-name="cms"
                        label="CMS"
                        :options="cms"
                        required
                    >
                        <template #buttonContent>
                            {{ form.cms.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-if="
                            form.cms.value === 'Magento' ||
                            form.cms.value === 'Prestashop' ||
                            form.cms.value === 'WooCommerce'
                        "
                        v-model="form.url"
                        label="URL"
                        field-name="url"
                        field-id="url"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'Shopify'"
                        v-model="form.storeName"
                        label="Nom de la boutique"
                        field-name="storeName"
                        field-id="storeName"
                        required
                    />

                    <InputField
                        v-if="
                            form.cms.value === 'Magento' ||
                            form.cms.value === 'Prestashop'
                        "
                        v-model="form.key"
                        label="Clé"
                        field-name="key"
                        field-id="key"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'Shopify'"
                        v-model="form.adminApiAccessToken"
                        label="Admin API access token"
                        field-name="adminApiAccessToken"
                        field-id="adminApiAccessToken"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'WooCommerce'"
                        v-model="form.consumerKey"
                        label="Clé client"
                        field-name="consumerKey"
                        field-id="consumerKey"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'WooCommerce'"
                        v-model="form.consumerSecret"
                        label="Clé secrète client"
                        field-name="consumerSecret"
                        field-id="consumerSecret"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'Hiboutik'"
                        v-model="form.account"
                        label="Compte"
                        field-name="account"
                        field-id="account"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'Hiboutik'"
                        v-model="form.apiLogin"
                        label="Identifiant API"
                        field-name="apiLogin"
                        field-id="apiLogin"
                        required
                    />

                    <InputField
                        v-if="form.cms.value === 'Hiboutik'"
                        v-model="form.apiKey"
                        label="Clé API"
                        field-name="apiKey"
                        field-id="apiKey"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="3">
                    <ListboxField
                        v-if="
                            form.cms.value === 'Magento' ||
                            form.cms.value === 'Prestashop'
                        "
                        v-model="form.stores"
                        field-id="stores"
                        field-name="stores"
                        label="Boutiques"
                        :options="stores"
                        required
                        multiple
                        :disabled="!stores.length"
                    >
                        <template #buttonContent>
                            <template v-if="form.stores.length > 0">
                                {{
                                    form.stores
                                        .map((store) => store.name)
                                        .join(', ')
                                }}
                            </template>

                            <template v-else> &nbsp;</template>
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-if="
                            form.cms.value === 'Shopify' ||
                            form.cms.value === 'WooCommerce' ||
                            form.cms.value === 'Hiboutik'
                        "
                        v-model="form.store"
                        field-id="store"
                        field-name="store"
                        label="Boutique"
                        :options="stores"
                        required
                        disabled
                    >
                        <template #buttonContent>
                            <template v-if="form.store">
                                {{ form.store.name }}
                            </template>

                            <template v-else> &nbsp;</template>
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-model="form.defaultLanguage"
                        field-id="defaultLanguage"
                        field-name="defaultLanguage"
                        label="Langue par défaut"
                        :options="languages"
                        required
                        :disabled="!languages.length"
                    >
                        <template #buttonContent>
                            <template v-if="form.defaultLanguage">
                                {{ form.defaultLanguage.name }}
                            </template>

                            <template v-else> &nbsp;</template>
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-if="
                            form.cms.value === 'Magento' ||
                            form.cms.value === 'Prestashop'
                        "
                        v-model="form.defaultStore"
                        field-id="defaultStore"
                        field-name="defaultStore"
                        label="Boutique par défaut"
                        :options="stores"
                        required
                        :disabled="!stores.length"
                    >
                        <template #buttonContent>
                            <template v-if="form.defaultStore">
                                {{ form.defaultStore.name }}
                            </template>

                            <template v-else> &nbsp;</template>
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ListboxField>
                </FormGrid>

                <FormGrid :columns="3">
                    <ListboxField
                        v-if="
                            form.cms.value === 'Magento' ||
                            form.cms.value === 'Prestashop'
                        "
                        v-model="form.dataType"
                        field-id="dataType"
                        field-name="dataType"
                        label="Type de données"
                        :options="dataTypes"
                        required
                        :disabled="!hasBaseValues"
                    >
                        <template #buttonContent>
                            {{ form.dataType.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-model="form.currencyCode"
                        field-id="currencyCode"
                        field-name="currencyCode"
                        label="Devise"
                        :options="currencyCodes"
                        required
                    >
                        <template #buttonContent>
                            {{ form.currencyCode.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>
            </FormContent>

            <FormContent
                v-if="
                    form.cms.value === 'Magento' ||
                    form.cms.value === 'Prestashop' ||
                    form.cms.value === 'WooCommerce'
                "
            >
                <FormGrid :columns="2">
                    <ComboboxField
                        v-model="form.productAttributes"
                        :options="productAttributes"
                        label="Attributs produit"
                        field-name="productAttributes"
                        field-id="productAttributes"
                        filteredOptionKey="code"
                        has-empty-option
                        multiple
                        :disabled="!productAttributes.length"
                    >
                        <template #optionContent="{ option }">
                            {{ option.code }}
                        </template>
                    </ComboboxField>

                    <ComboboxField
                        v-if="form.cms.value === 'Prestashop'"
                        v-model="form.productFeatures"
                        :options="productFeatures"
                        label="Caractéristiques produit"
                        field-name="productFeatures"
                        field-id="productFeatures"
                        filteredOptionKey="code"
                        has-empty-option
                        multiple
                        :disabled="!productFeatures.length"
                    >
                        <template #optionContent="{ option }">
                            {{ option.code }}
                        </template>
                    </ComboboxField>
                </FormGrid>
            </FormContent>

            <FormContent v-if="form.cms.value === 'Magento'">
                <SwitchField
                    v-model="form.doNotUseInvoices"
                    label="Ne pas utiliser les factures"
                    field-name="doNotUseInvoices"
                    field-id="doNotUseInvoices"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <ActionButton type="secondary" :callback="() => updateData()">
                    Mettre à jour les données
                </ActionButton>
            </template>
        </FormFooter>
    </FormRoot>
</template>
